import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Badge,
  Drawer,
  Breadcrumbs,
  Checkbox,
  Snackbar,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  AccountTree as TreeIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Language as LanguageIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  QuestionAnswer as QuestionIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEvents as TrophyIcon,
  FormatListBulleted as BulkIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Link as LinkIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  History as HistoryIcon,
  FilterAlt as FilterAltIcon,
  // SERP Feature icons
  AutoAwesome as AiOverviewIcon,
  FormatQuote as FeaturedSnippetIcon,
  Info as KnowledgeGraphIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocationOn as LocalPackIcon,
  ShoppingCart as ShoppingIcon,
  Campaign as AdsIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  HelpOutline as PaaIcon,
  Assessment as ImpactIcon
} from '@mui/icons-material';
import { RootState } from '../../store';
import WebsiteSelector from '../../components/common/WebsiteSelector';
import { backendApiService } from '../../services/backendApiService';
import { keywordService, KeywordIdea } from '../../services/keywordService';
import { websiteService } from '../../services/websiteService';
import { urlService } from '../../services/urlService';
import { Website, Url } from '../../types';
import {
  competitorService,
  CompetitorOverview,
  KeywordCompetition,
  CompetitorInsight,
  WebsiteCompetitor
} from '../../services/competitorService';
import { paaTreeApiService, PAATree } from '../../services/paaTreeApiService';
import { keywordCacheService, CachedKeywordData } from '../../services/keywordCacheService';
import { getCountryFlag } from '../../utils/countryFlags';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';

// Types
interface SerpResult {
  position: number;
  title: string;
  link: string;
  snippet: string;
  displayed_link: string;
}

interface FeatureOrderItem {
  feature: string;
  position: 'top' | 'bottom' | 'inline' | 'sidebar';
  apiKey: string;
}

interface SerpFeatures {
  has_ai_overview: boolean;
  has_featured_snippet: boolean;
  has_knowledge_graph: boolean;
  has_local_pack: boolean;
  has_shopping: boolean;
  has_top_ads: boolean;
  has_images: boolean;
  has_videos: boolean;
  has_people_also_ask: boolean;
  impact_score: number;
  impact_level: 'VERY LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY HIGH';
  // Dynamic feature order from API (order they appear on Google)
  feature_order?: FeatureOrderItem[];
  // Raw data (optional)
  ai_overview?: any;
  featured_snippet?: any;
  knowledge_graph?: any;
  local_pack?: any;
  shopping?: any;
  top_ads?: any;
  images?: any;
  videos?: any;
}

interface SearchResponse {
  keyword: string;
  country: string;
  device: string;
  search_url: string;
  search_metadata: {
    total_results: string;
    time_taken: string;
    query_displayed: string;
    google_url: string;
  };
  organic_results: SerpResult[];
  people_also_ask?: Array<{
    question: string;
    answer: string;
    link: string;
  }>;
  related_searches?: Array<{
    query: string;
  }>;
  serp_features?: SerpFeatures;
}

// Constants
const countries = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'COM_US', name: 'United States (.com)', flag: '🇺🇸' },
];

const OUR_DOMAINS = [
  'newyork.co.uk', 'newyork.nl', 'newyorkcity.de', 'newyorkcity.fr',
  'newyork.com.br', 'newyorkcity.fi', 'newyork.se', 'newyorkcity.it',
  'newyorkcity.jp', 'newyorkcity.es', 'www.newyork.com', 'newyork.ca',
  'newyork.com.au', 'newyork.no', 'newyork.co.kr', 'newyorkcity.dk',
  'newyork.ru', 'newyorkcity.cn'
];

const OFFICIAL_DOMAINS = [
  'nba.com', 'ticketmaster.com', 'ticketmaster.co.uk', 'stubhub.com',
  'seatgeek.com', 'vividseats.com', 'viagogo.com', 'tickpick.com',
  'tripadvisor.com', 'tripadvisor.co.uk', 'viator.com', 'getyourguide.com',
  'booking.com', 'expedia.com', 'hotels.com', 'airbnb.com', 'timeout.com',
  'lonelyplanet.com', 'wikipedia.org', 'wikivoyage.org', '.gov', 'nps.gov', 'nyc.gov'
];

// Keyword Intent Classification (heuristic-based)
type KeywordIntent = 'N' | 'I' | 'C' | 'T';

interface IntentResult {
  intent: KeywordIntent;
  label: string;
  color: string;
  bgColor: string;
  description: string;
}

const classifyKeywordIntent = (keyword: string): IntentResult => {
  const kw = keyword.toLowerCase().trim();

  // Transactional patterns (user ready to buy/book)
  const transactionalPatterns = [
    /\b(buy|purchase|order|book|reserve|get tickets|ticket|tickets)\b/,
    /\b(price|pricing|cost|cheap|cheapest|discount|deal|deals|coupon|promo)\b/,
    /\b(for sale|on sale|shop|store|checkout)\b/,
    /\b(subscribe|subscription|membership|register|sign up)\b/,
    /\b(download|install|free trial)\b/
  ];

  // Commercial patterns (user researching before buying)
  const commercialPatterns = [
    /\b(best|top|vs|versus|compare|comparison|review|reviews|rating|ratings)\b/,
    /\b(recommended|recommendation|alternatives|alternative)\b/,
    /\b(worth it|should i|which|what to buy)\b/,
    /\b(pros and cons|features|benefits)\b/,
    /\b(guide to buying|buying guide)\b/
  ];

  // Informational patterns (user wants to learn)
  const informationalPatterns = [
    /^(how|what|why|when|where|who|which|can|does|do|is|are|will|should)\b/,
    /\b(guide|tutorial|tips|learn|explained|explanation|definition|meaning)\b/,
    /\b(how to|what is|what are|why do|why does|when to|where to)\b/,
    /\b(example|examples|idea|ideas|inspiration)\b/,
    /\b(history|facts|information|info|about)\b/,
    /\b(map|hours|schedule|directions|address|location)\b/
  ];

  // Navigational patterns (user looking for specific site)
  const navigationalPatterns = [
    /\b(login|log in|signin|sign in|account|my account)\b/,
    /\b(official|website|site|homepage|home page)\b/,
    /\b(contact|support|customer service|help desk)\b/,
    /^(facebook|twitter|instagram|youtube|linkedin|amazon|google|netflix)/,
    /\.(com|org|net|co\.uk|nl|de|fr)$/
  ];

  // Check patterns in order of specificity
  for (const pattern of transactionalPatterns) {
    if (pattern.test(kw)) {
      return {
        intent: 'T',
        label: 'Transactional',
        color: '#D32F2F',
        bgColor: 'rgba(211, 47, 47, 0.1)',
        description: 'User ready to take action (buy, book, download)'
      };
    }
  }

  for (const pattern of commercialPatterns) {
    if (pattern.test(kw)) {
      return {
        intent: 'C',
        label: 'Commercial',
        color: '#1976D2',
        bgColor: 'rgba(25, 118, 210, 0.1)',
        description: 'User researching before making a decision'
      };
    }
  }

  for (const pattern of navigationalPatterns) {
    if (pattern.test(kw)) {
      return {
        intent: 'N',
        label: 'Navigational',
        color: '#7B1FA2',
        bgColor: 'rgba(123, 31, 162, 0.1)',
        description: 'User looking for a specific website or page'
      };
    }
  }

  for (const pattern of informationalPatterns) {
    if (pattern.test(kw)) {
      return {
        intent: 'I',
        label: 'Informational',
        color: '#388E3C',
        bgColor: 'rgba(56, 142, 60, 0.1)',
        description: 'User seeking information or knowledge'
      };
    }
  }

  // Default to Informational for general queries
  return {
    intent: 'I',
    label: 'Informational',
    color: '#388E3C',
    bgColor: 'rgba(56, 142, 60, 0.1)',
    description: 'User seeking information or knowledge'
  };
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`keyword-intelligence-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const KeywordIntelligencePage: React.FC = () => {
  const navigate = useNavigate();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);

  // Main state
  const [tabValue, setTabValue] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState('UK');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');

  // SERP Check state
  const [serpLoading, setSerpLoading] = useState(false);
  const [serpError, setSerpError] = useState<string | null>(null);
  const [serpResults, setSerpResults] = useState<SearchResponse | null>(null);

  // SERP Screenshot state (auto-fetched using task ID - FREE)
  const [serpScreenshot, setSerpScreenshot] = useState<string | null>(null);
  const [serpScreenshotLoading, setSerpScreenshotLoading] = useState(false);
  const [serpTaskId, setSerpTaskId] = useState<string | null>(null);

  // Keyword Research state
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);
  const [keywordIdeas, setKeywordIdeas] = useState<KeywordIdea[]>([]);

  // Competitors state
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorsError, setCompetitorsError] = useState<string | null>(null);
  const [competitorOverview, setCompetitorOverview] = useState<CompetitorOverview[]>([]);
  const [keywordCompetition, setKeywordCompetition] = useState<KeywordCompetition[]>([]);
  const [insights, setInsights] = useState<CompetitorInsight[]>([]);
  const [websiteCompetitors, setWebsiteCompetitors] = useState<WebsiteCompetitor[]>([]);
  const [expandedKeywords, setExpandedKeywords] = useState<Set<string>>(new Set());

  // PAA state
  const [paaLoading, setPaaLoading] = useState(false);
  const [paaError, setPaaError] = useState<string | null>(null);
  const [paaTrees, setPaaTrees] = useState<PAATree[]>([]);
  const [paaSearchQuery, setPaaSearchQuery] = useState('');

  // Drill-down state (for viewing SERP from keyword click)
  const [drillDownKeyword, setDrillDownKeyword] = useState<string | null>(null);
  const [drillDownOpen, setDrillDownOpen] = useState(false);

  // Bulk search state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkKeywords, setBulkKeywords] = useState('');

  // Save keywords to URL state
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [availableWebsites, setAvailableWebsites] = useState<Website[]>([]);
  const [selectedTargetWebsite, setSelectedTargetWebsite] = useState<Website | null>(null);
  const [availableUrls, setAvailableUrls] = useState<Url[]>([]);
  const [selectedTargetUrl, setSelectedTargetUrl] = useState<Url | null>(null);
  const [savingKeywords, setSavingKeywords] = useState(false);
  const [loadingWebsites, setLoadingWebsites] = useState(false);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Keyword context state - the keyword being researched that contextualizes other tabs
  const [activeKeywordContext, setActiveKeywordContext] = useState<string | null>(null);
  const [cachedKeywordData, setCachedKeywordData] = useState<CachedKeywordData | null>(null);

  // View mode toggles for tabs - 'keyword' shows data for active keyword, 'historical' shows all website data
  const [competitorsViewMode, setCompetitorsViewMode] = useState<'keyword' | 'historical'>('keyword');
  const [paaViewMode, setPaaViewMode] = useState<'keyword' | 'historical'>('keyword');

  // Sorting state for keyword table
  const [sortField, setSortField] = useState<'vol' | 'comp' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Competitor analysis state
  const [competitorAnalysis, setCompetitorAnalysis] = useState<any>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // SERP Features info popover
  const [serpFeaturesInfoAnchor, setSerpFeaturesInfoAnchor] = useState<HTMLElement | null>(null);

  // Utility functions - proper domain matching (not substring!)
  const isOurDomain = (domain: string): boolean => {
    const d = domain.toLowerCase().replace(/^www\./, '');
    return OUR_DOMAINS.some(ourDomain => {
      const od = ourDomain.toLowerCase().replace(/^www\./, '');
      // Exact match or subdomain match
      return d === od || d.endsWith(`.${od}`);
    });
  };

  const isOurWebsite = (url: string): boolean => {
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      return isOurDomain(hostname);
    } catch {
      return false;
    }
  };

  const isOfficialDomainMatch = (domain: string): boolean => {
    const d = domain.toLowerCase().replace(/^www\./, '');
    return OFFICIAL_DOMAINS.some(officialDomain => {
      const od = officialDomain.toLowerCase().replace(/^www\./, '');
      return d === od || d.endsWith(`.${od}`) || d.includes(od);
    });
  };

  const isOfficialWebsite = (url: string): boolean => {
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      return isOfficialDomainMatch(hostname);
    } catch {
      return false;
    }
  };

  // Forum detection (Reddit, Facebook, Quora, etc.) - not real SEO competitors
  const isForumSite = (url: string): { isForum: boolean; platform: string | null } => {
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      if (hostname.includes('reddit.com')) return { isForum: true, platform: 'reddit' };
      if (hostname.includes('facebook.com')) return { isForum: true, platform: 'facebook' };
      if (hostname.includes('quora.com')) return { isForum: true, platform: 'quora' };
      return { isForum: false, platform: null };
    } catch {
      return { isForum: false, platform: null };
    }
  };

  const isOfficialDomain = (domain: string): boolean => {
    return websiteCompetitors.some(comp =>
      comp.competitor_domain === domain && comp.priority === 999
    );
  };

  // Load competitor data when website changes
  useEffect(() => {
    if (selectedWebsite && tabValue === 1) {
      loadCompetitorData();
    }
  }, [selectedWebsite, tabValue]);

  // Load PAA trees when website changes
  useEffect(() => {
    if (selectedWebsite && tabValue === 2) {
      loadPaaTrees();
    }
  }, [selectedWebsite, tabValue]);

  // Load websites when save dialog opens
  useEffect(() => {
    if (saveDialogOpen && availableWebsites.length === 0) {
      loadWebsites();
    }
  }, [saveDialogOpen]);

  // Load URLs when target website is selected
  useEffect(() => {
    if (selectedTargetWebsite) {
      loadUrlsForWebsite(selectedTargetWebsite.website_id);
    } else {
      setAvailableUrls([]);
      setSelectedTargetUrl(null);
    }
  }, [selectedTargetWebsite]);

  // Auto-fetch HTML snapshot when we have a task_id (FREE - no extra API credit)
  useEffect(() => {
    const fetchHtmlByTaskId = async () => {
      if (!serpTaskId) {
        setSerpScreenshot(null);
        return;
      }

      setSerpScreenshotLoading(true);
      setSerpScreenshot(null);

      try {
        // Use task ID to get HTML for FREE (no extra API credit)
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`/api/serphouse/html-by-id/${serpTaskId}?domain=google.co.uk`, {
          headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
        });
        const data = await response.json();

        if (data.success && data.html) {
          console.log(`[SERP HTML] Loaded HTML snapshot (${data.size_kb}KB) - FREE via task ID`);
          setSerpScreenshot(data.html);
        }
      } catch (error) {
        console.error('Error fetching SERP HTML by task ID:', error);
      } finally {
        setSerpScreenshotLoading(false);
      }
    };

    fetchHtmlByTaskId();
  }, [serpTaskId]);

  // Load available websites
  const loadWebsites = async () => {
    setLoadingWebsites(true);
    try {
      const { websites, error } = await websiteService.getWebsites();
      if (error) {
        console.error('Error loading websites:', error);
      } else {
        setAvailableWebsites(websites);
      }
    } catch (err) {
      console.error('Error loading websites:', err);
    } finally {
      setLoadingWebsites(false);
    }
  };

  // Load URLs for a specific website
  const loadUrlsForWebsite = async (websiteId: string) => {
    setLoadingUrls(true);
    setAvailableUrls([]);
    setSelectedTargetUrl(null);

    try {
      const { urls, error } = await urlService.getUrlsByWebsite(websiteId);
      if (error) {
        console.error('Error loading URLs:', error);
      } else {
        setAvailableUrls(urls);
      }
    } catch (err) {
      console.error('Error loading URLs:', err);
    } finally {
      setLoadingUrls(false);
    }
  };

  // Handle keyword checkbox selection
  const handleKeywordCheckboxChange = (kw: string, checked: boolean) => {
    const newSelected = new Set(selectedKeywords);
    if (checked) {
      newSelected.add(kw);
    } else {
      newSelected.delete(kw);
    }
    setSelectedKeywords(newSelected);
  };

  // Handle select all keywords
  const handleSelectAllKeywords = (checked: boolean) => {
    if (checked) {
      const allKeywords = new Set(keywordIdeas.map(k => k.keyword));
      setSelectedKeywords(allKeywords);
    } else {
      setSelectedKeywords(new Set());
    }
  };

  // Save selected keywords to URL
  const handleSaveKeywordsToUrl = async () => {
    if (!selectedTargetWebsite || !selectedTargetUrl || selectedKeywords.size === 0) {
      setSnackbar({ open: true, message: 'Please select a website, URL, and at least one keyword', severity: 'error' });
      return;
    }

    setSavingKeywords(true);
    try {
      const keywordsToSave = Array.from(selectedKeywords);

      // Use the backend API to save keywords
      const response = await backendApiService.post('/api/keywords/bulk-add', {
        url_id: selectedTargetUrl.url_id,
        keywords: keywordsToSave
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to save keywords');
      }

      setSnackbar({
        open: true,
        message: `Successfully saved ${keywordsToSave.length} keywords to ${selectedTargetUrl.path}`,
        severity: 'success'
      });
      setSaveDialogOpen(false);
      setSelectedKeywords(new Set());
    } catch (err: any) {
      console.error('Error saving keywords:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to save keywords', severity: 'error' });
    } finally {
      setSavingKeywords(false);
    }
  };

  // SERP Check handler
  const handleSerpSearch = async (searchKeyword?: string) => {
    const kw = searchKeyword || keyword;
    if (!kw.trim()) {
      setSerpError('Please enter a keyword to search');
      return;
    }

    // DEBUG: Log search attempt for troubleshooting
    console.log('[SERP Search] Starting search:', { keyword: kw, country, device, timestamp: new Date().toISOString() });

    setSerpLoading(true);
    setSerpError(null);
    setSerpResults(null);

    try {
      console.log('[SERP Search] Making API call...');
      const response = await backendApiService.post('/api/serphouse/search', {
        keyword: kw.trim(),
        country,
        device,
      });

      console.log('[SERP Search] Response received:', {
        success: response.success,
        hasData: !!response.data,
        organicCount: response.data?.organic_results?.length || 0,
        taskId: response.task_id || null
      });

      if (response.success) {
        setSerpResults(response.data);
        // Capture task_id for free HTML retrieval
        setSerpTaskId(response.task_id || null);
        // Set active keyword context for other tabs
        setActiveKeywordContext(kw.trim());
        // Cache the results for instant access when switching tabs
        const paaQuestions = response.data?.people_also_ask?.map((p: any) => p.question) || [];
        const relatedSearches = response.data?.related_searches?.map((r: any) => r.query) || [];
        keywordCacheService.set(
          kw.trim(),
          country,
          device,
          response.data?.organic_results || [],
          paaQuestions,
          relatedSearches
        );
        // Update cached data state for immediate use
        setCachedKeywordData(keywordCacheService.get(kw.trim(), country, device));
      } else {
        console.error('[SERP Search] API returned failure:', response);
        setSerpError(response.message || response.error || 'Failed to fetch search results');
      }
    } catch (err: any) {
      console.error('[SERP Search] Exception caught:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      });
      setSerpError(err.response?.data?.message || err.response?.data?.error || 'An error occurred while fetching results');
    } finally {
      setSerpLoading(false);
    }
  };

  // Keyword Research handler
  const handleKeywordResearch = async () => {
    // Get keywords based on mode
    let keywordsToSearch: string[] = [];

    if (bulkMode) {
      keywordsToSearch = bulkKeywords
        .split('\n')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      if (keywordsToSearch.length === 0) {
        setResearchError('Please enter at least one keyword');
        return;
      }

      if (keywordsToSearch.length > 100) {
        setResearchError('Please enter no more than 100 keywords at once');
        return;
      }
    } else {
      if (!keyword.trim()) {
        setResearchError('Please enter a keyword');
        return;
      }
      keywordsToSearch = [keyword.trim()];
    }

    setResearchLoading(true);
    setResearchError(null);

    try {
      const countryToLanguage: { [key: string]: string } = {
        'UK': 'en', 'NL': 'nl', 'FR': 'fr', 'DE': 'de', 'IT': 'it',
        'ES': 'es', 'SE': 'sv', 'NO': 'no', 'DK': 'da', 'FI': 'fi',
        'BR': 'pt', 'JP': 'ja', 'CA': 'en', 'AU': 'en', 'KR': 'ko',
        'RU': 'ru', 'CN': 'zh', 'COM_US': 'en'
      };

      const result = await keywordService.getKeywordIdeas(
        keywordsToSearch,
        country,
        countryToLanguage[country] || 'en'
      );

      if (result.error) {
        setResearchError(result.error);
      } else {
        setKeywordIdeas(result.keywords);
        // Auto-trigger SERP for the first keyword
        if (result.keywords.length > 0) {
          handleSerpSearch(keywordsToSearch[0]);
        }
      }
    } catch (err: any) {
      setResearchError(err.message || 'Failed to fetch keyword ideas');
    } finally {
      setResearchLoading(false);
    }
  };

  // Load competitor data
  const loadCompetitorData = async () => {
    if (!selectedWebsite) return;

    setCompetitorsLoading(true);
    setCompetitorsError(null);

    try {
      const [overviewResult, competitionResult, insightsResult, websiteCompResult] = await Promise.all([
        competitorService.getCompetitorOverview(selectedWebsite.website_id),
        competitorService.getKeywordCompetition(selectedWebsite.website_id),
        competitorService.generateInsights(selectedWebsite.website_id),
        competitorService.getWebsiteCompetitors(selectedWebsite.website_id)
      ]);

      if (overviewResult.error) {
        setCompetitorsError(overviewResult.error);
      } else {
        setCompetitorOverview(overviewResult.overview);
      }

      if (!competitionResult.error) {
        setKeywordCompetition(competitionResult.competition);
      }

      if (!insightsResult.error) {
        setInsights(insightsResult.insights);
      }

      if (!websiteCompResult.error) {
        setWebsiteCompetitors(websiteCompResult.competitors);
      }
    } catch (err) {
      setCompetitorsError('Failed to load competitor data');
    } finally {
      setCompetitorsLoading(false);
    }
  };

  // Load PAA trees
  const loadPaaTrees = async () => {
    if (!selectedWebsite) return;

    setPaaLoading(true);
    setPaaError(null);

    try {
      const { trees: data, error } = await paaTreeApiService.getTrees(
        selectedWebsite.website_id
      );

      if (error) {
        setPaaError(error);
      } else {
        setPaaTrees(data);
      }
    } catch (err: any) {
      setPaaError(err.message || 'Failed to load PAA trees');
    } finally {
      setPaaLoading(false);
    }
  };

  // Analyze competitors with Claude
  const handleCompetitorAnalysis = async (forceRefresh: boolean = false) => {
    if (!activeKeywordContext || !cachedKeywordData) return;

    setAnalysisLoading(true);
    setAnalysisError(null);
    setCompetitorAnalysis(null);

    try {
      // Find our URL if we're ranking
      const ourCompetitor = cachedKeywordData.competitorDomains.find(c =>
        isOurDomain(c.domain)
      );

      // Get beatable competitors (not official, not us)
      const beatableCompetitors = cachedKeywordData.competitorDomains
        .filter(c =>
          !isOfficialDomainMatch(c.domain) && !isOurDomain(c.domain)
        )
        .slice(0, 5);

      const response = await backendApiService.post('/api/competitor-analysis/analyze', {
        keyword: activeKeywordContext,
        ourUrl: ourCompetitor?.url || null,
        competitors: beatableCompetitors.map(c => ({
          position: c.position,
          url: c.url,
          domain: c.domain,
          title: c.title
        })),
        country,
        maxCompetitors: 5,
        forceRefresh
      });

      if (response.success) {
        setCompetitorAnalysis(response.analysis);
      } else {
        setAnalysisError(response.error || 'Analysis failed');
      }
    } catch (err: any) {
      console.error('[CompetitorAnalysis] Error:', err);
      setAnalysisError(err.response?.data?.error || err.message || 'Failed to analyze competitors');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Refresh rankings: clear cache and re-fetch SERP data
  const handleRefreshRankings = async () => {
    if (!activeKeywordContext) return;

    // Clear the cache for this keyword
    keywordCacheService.clear(activeKeywordContext, country, device);

    // Clear any existing analysis
    setCompetitorAnalysis(null);
    setAnalysisError(null);
    setCachedKeywordData(null);

    // Re-fetch SERP data
    await handleSerpSearch(activeKeywordContext);
  };

  // Drill-down: View SERP for a specific keyword
  const handleKeywordClick = (kw: string) => {
    setDrillDownKeyword(kw);
    setKeyword(kw);
    // Only open drawer if not on Research tab (tab 0 shows SERP inline)
    if (tabValue !== 0) {
      setDrillDownOpen(true);
    }
    handleSerpSearch(kw);
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleKeywordExpansion = (kw: string) => {
    const newExpanded = new Set(expandedKeywords);
    if (newExpanded.has(kw)) {
      newExpanded.delete(kw);
    } else {
      newExpanded.add(kw);
    }
    setExpandedKeywords(newExpanded);
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <LightbulbIcon color="primary" />;
      case 'threat': return <WarningIcon color="error" />;
      case 'action': return <CheckCircleIcon color="success" />;
      default: return <LightbulbIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const filteredPaaTrees = paaSearchQuery
    ? paaTrees.filter(tree => tree.root_keyword.toLowerCase().includes(paaSearchQuery.toLowerCase()))
    : paaTrees;

  // Sorted keyword ideas based on sortField and sortDirection
  const sortedKeywordIdeas = React.useMemo(() => {
    if (!sortField) return keywordIdeas;

    const competitionOrder = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };

    return [...keywordIdeas].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'vol') {
        comparison = (a.avgMonthlySearches || 0) - (b.avgMonthlySearches || 0);
      } else if (sortField === 'comp') {
        comparison = (competitionOrder[a.competition] || 0) - (competitionOrder[b.competition] || 0);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [keywordIdeas, sortField, sortDirection]);

  // Handle sort header click - cycles through: none -> desc -> asc -> none
  const handleSortClick = (field: 'vol' | 'comp') => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortField(null);
      setSortDirection('desc');
    }
  };

  // Get sort icon for table header
  const getSortIcon = (field: 'vol' | 'comp') => {
    if (sortField !== field) return null;
    return sortDirection === 'desc'
      ? <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
      : <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5 }} />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon sx={{ fontSize: 36, color: '#cc081c' }} />
              Keyword Intelligence
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Research keywords, check rankings, analyze competitors, and explore questions - all in one place
            </Typography>
          </Box>
          <WebsiteSelector showAddButton={false} />
        </Box>
      </Box>

      {/* Universal Search Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Bulk Mode Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={bulkMode ? 'bulk' : 'single'}
            exclusive
            onChange={(e, val) => val && setBulkMode(val === 'bulk')}
            size="small"
          >
            <ToggleButton value="single">
              <Tooltip title="Single Keyword">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SearchIcon sx={{ mr: 0.5 }} /> Single
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="bulk">
              <Tooltip title="Bulk Keywords (one per line)">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BulkIcon sx={{ mr: 0.5 }} /> Bulk
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {bulkMode && (
            <Typography variant="caption" color="text.secondary">
              Enter one keyword per line (max 100)
            </Typography>
          )}
        </Box>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, md: bulkMode ? 12 : 5 }}>
            {bulkMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Enter keywords (one per line)"
                variant="outlined"
                value={bulkKeywords}
                onChange={(e) => setBulkKeywords(e.target.value)}
                placeholder={`Empire State Building tickets\nStatue of Liberty tour\nCentral Park walking tour`}
              />
            ) : (
              <TextField
                fullWidth
                label="Enter keyword"
                variant="outlined"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    if (tabValue === 0) handleKeywordResearch();
                    else if (tabValue === 1) handleSerpSearch();
                  }
                }}
                placeholder="e.g., Empire State Building tickets"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Grid>

          {!bulkMode && (
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={country}
                  label="Country"
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{c.flag}</span>
                        <span>{c.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {!bulkMode && (
            <Grid size={{ xs: 12, md: 2 }}>
              <ToggleButtonGroup
                value={device}
                exclusive
                onChange={(e, val) => val && setDevice(val)}
                fullWidth
              >
                <ToggleButton value="desktop">
                  <Tooltip title="Desktop"><ComputerIcon /></Tooltip>
                </ToggleButton>
                <ToggleButton value="mobile">
                  <Tooltip title="Mobile"><PhoneIcon /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: bulkMode ? 12 : 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: bulkMode ? 'row' : 'column' }}>
              {bulkMode && (
                <>
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={country}
                      label="Country"
                      onChange={(e) => setCountry(e.target.value)}
                      size="medium"
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{c.flag}</span>
                            <span>{c.name}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <ToggleButtonGroup
                    value={device}
                    exclusive
                    onChange={(e, val) => val && setDevice(val)}
                  >
                    <ToggleButton value="desktop">
                      <Tooltip title="Desktop"><ComputerIcon /></Tooltip>
                    </ToggleButton>
                    <ToggleButton value="mobile">
                      <Tooltip title="Mobile"><PhoneIcon /></Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </>
              )}
              <Button
                fullWidth={!bulkMode}
                variant="contained"
                onClick={() => {
                  if (tabValue === 0) handleKeywordResearch();
                  else if (tabValue === 1) handleSerpSearch();
                }}
                disabled={serpLoading || researchLoading}
                sx={{ height: '56px', bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' }, minWidth: bulkMode ? 120 : 'auto' }}
              >
                {(serpLoading || researchLoading) ? <CircularProgress size={24} /> : 'Search'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs - 3 tabs: Research, Competitors, PAA */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { py: 1.5 },
            '& .Mui-selected': { color: '#cc081c' },
            '& .MuiTabs-indicator': { bgcolor: '#cc081c' }
          }}
        >
          <Tab
            icon={<TrendingUpIcon />}
            label="Research & SERP"
            iconPosition="start"
          />
          <Tab
            icon={<PeopleIcon />}
            label="Competitors"
            iconPosition="start"
          />
          <Tab
            icon={<TreeIcon />}
            label="PAA Trees"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab 0: Keyword Research - Side by Side Layout */}
      <TabPanel value={tabValue} index={0}>
        {researchError && <Alert severity="error" sx={{ mb: 2 }}>{researchError}</Alert>}

        {keywordIdeas.length === 0 && !researchLoading && !serpResults && (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Discover Keyword Opportunities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a seed keyword above to find related keywords with search volume and competition data
            </Typography>
          </Paper>
        )}

        {(keywordIdeas.length > 0 || serpResults) && (
          <Grid container spacing={2}>
            {/* Left: Keywords Table - Compact */}
            <Grid size={{ xs: 12, md: 5 }}>
              {/* Toolbar for selected keywords */}
              {keywordIdeas.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selectedKeywords.size === keywordIdeas.length && keywordIdeas.length > 0}
                      indeterminate={selectedKeywords.size > 0 && selectedKeywords.size < keywordIdeas.length}
                      onChange={(e) => handleSelectAllKeywords(e.target.checked)}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={() => selectedKeywords.size > 0 ? setSelectedKeywords(new Set()) : handleSelectAllKeywords(true)}>
                      {selectedKeywords.size > 0 ? `${selectedKeywords.size} selected` : 'Select all'}
                    </Typography>
                  </Box>
                  {selectedKeywords.size > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={() => setSaveDialogOpen(true)}
                      sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
                    >
                      Save to URL
                    </Button>
                  )}
                </Box>
              )}
              <Paper sx={{ height: 'calc(100vh - 380px)', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 1, width: 40 }}></TableCell>
                      <TableCell sx={{ py: 1, fontWeight: 600 }}>Keyword</TableCell>
                      <TableCell align="center" sx={{ py: 1, fontWeight: 600, width: 60 }}>
                        <Tooltip title="Keyword Intent: N=Navigational, I=Informational, C=Commercial, T=Transactional">
                          <span>Intent</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          py: 1,
                          fontWeight: 600,
                          width: 80,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleSortClick('vol')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          Vol
                          {getSortIcon('vol')}
                          {sortField !== 'vol' && (
                            <FilterAltIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.3 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          py: 1,
                          fontWeight: 600,
                          width: 80,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleSortClick('comp')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Comp
                          {getSortIcon('comp')}
                          {sortField !== 'comp' && (
                            <FilterAltIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.3 }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedKeywordIdeas.slice(0, 50).map((idea, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        selected={idea.keyword === drillDownKeyword}
                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'primary.50' } }}
                      >
                        <TableCell sx={{ py: 0.5, px: 1 }} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedKeywords.has(idea.keyword)}
                            onChange={(e) => handleKeywordCheckboxChange(idea.keyword, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }} onClick={() => handleKeywordClick(idea.keyword)}>
                          <Typography variant="body2" sx={{ fontWeight: idea.keyword === drillDownKeyword ? 600 : 400 }}>
                            {idea.keyword}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }} onClick={() => handleKeywordClick(idea.keyword)}>
                          {(() => {
                            const intentResult = classifyKeywordIntent(idea.keyword);
                            return (
                              <Tooltip title={intentResult.description}>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    bgcolor: intentResult.bgColor,
                                    color: intentResult.color,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    minWidth: 20,
                                    textAlign: 'center'
                                  }}
                                >
                                  {intentResult.intent}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.75 }} onClick={() => handleKeywordClick(idea.keyword)}>
                          <Typography variant="body2">
                            {idea.avgMonthlySearches?.toLocaleString() || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }} onClick={() => handleKeywordClick(idea.keyword)}>
                          <Chip
                            size="small"
                            label={idea.competition?.charAt(0) || '-'}
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: idea.competition === 'LOW' ? 'success.main' :
                                idea.competition === 'MEDIUM' ? 'warning.main' : 'error.main',
                              color: 'white'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Right: SERP Results - Inline */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper sx={{ height: 'calc(100vh - 340px)', overflow: 'auto', p: 2 }}>
                {serpLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : serpError ? (
                  <Alert severity="error">{serpError}</Alert>
                ) : serpResults ? (
                  <Box>
                    {/* Header with metadata */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        "{serpResults.keyword}"
                      </Typography>
                      {/* Keyword Intent Badge */}
                      {(() => {
                        const intentResult = classifyKeywordIntent(serpResults.keyword);
                        return (
                          <Tooltip title={`${intentResult.label}: ${intentResult.description}`}>
                            <Chip
                              label={intentResult.intent}
                              size="small"
                              sx={{
                                bgcolor: intentResult.bgColor,
                                color: intentResult.color,
                                fontWeight: 600,
                                minWidth: 28
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                      <Chip
                        label={`${serpResults.organic_results?.length || 0} results`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={device === 'mobile' ? 'Mobile' : 'Desktop'}
                        size="small"
                        variant="outlined"
                        icon={device === 'mobile' ? <PhoneIcon sx={{ fontSize: 14 }} /> : <ComputerIcon sx={{ fontSize: 14 }} />}
                      />
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<TrendingUpIcon />}
                          onClick={() => {
                            // Add current keyword to selection and open save dialog
                            const newSelected = new Set(selectedKeywords);
                            newSelected.add(serpResults.keyword);
                            setSelectedKeywords(newSelected);
                            setSaveDialogOpen(true);
                          }}
                          sx={{
                            bgcolor: '#10B981',
                            '&:hover': { bgcolor: '#059669' },
                            fontWeight: 600
                          }}
                        >
                          Track Keyword
                        </Button>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          href={serpResults.search_metadata?.google_url}
                          target="_blank"
                        >
                          Google
                        </Button>
                      </Box>
                    </Box>

                    {/* SERP Features Impact Score - Compact header */}
                    {serpResults.serp_features && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          SERP Impact:
                        </Typography>
                        <Chip
                          label={`${serpResults.serp_features.impact_score}/5 ${serpResults.serp_features.impact_level}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: serpResults.serp_features.impact_score <= 1 ? 'success.100' :
                                     serpResults.serp_features.impact_score <= 2 ? 'success.200' :
                                     serpResults.serp_features.impact_score <= 3 ? 'warning.100' :
                                     serpResults.serp_features.impact_score <= 4 ? 'warning.200' : 'error.100',
                            color: serpResults.serp_features.impact_score <= 2 ? 'success.800' :
                                   serpResults.serp_features.impact_score <= 3 ? 'warning.800' : 'error.800'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => setSerpFeaturesInfoAnchor(e.currentTarget)}
                          sx={{ p: 0.25 }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </IconButton>
                        <Popover
                          open={Boolean(serpFeaturesInfoAnchor)}
                          anchorEl={serpFeaturesInfoAnchor}
                          onClose={() => setSerpFeaturesInfoAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <Box sx={{ p: 2, maxWidth: 360 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              SERP Features Impact Score
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              Measures how much SERP features compete for visibility. Higher score = harder to rank organically.
                            </Typography>
                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                              How it's calculated:
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.25, fontSize: '0.75rem', mb: 1.5 }}>
                              <Typography variant="caption">AI Overview</Typography>
                              <Typography variant="caption" fontWeight={600} color="error.main">+1.5</Typography>
                              <Typography variant="caption">Featured Snippet</Typography>
                              <Typography variant="caption" fontWeight={600} color="warning.main">+1.0</Typography>
                              <Typography variant="caption">Shopping Results</Typography>
                              <Typography variant="caption" fontWeight={600} color="warning.main">+1.0</Typography>
                              <Typography variant="caption">Knowledge Panel</Typography>
                              <Typography variant="caption" fontWeight={600}>+0.5</Typography>
                              <Typography variant="caption">Local Pack</Typography>
                              <Typography variant="caption" fontWeight={600}>+0.5</Typography>
                              <Typography variant="caption">Top Ads</Typography>
                              <Typography variant="caption" fontWeight={600}>+0.5</Typography>
                              <Typography variant="caption">Images/Videos/PAA</Typography>
                              <Typography variant="caption" fontWeight={600}>+0.25 each</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                              Score meaning:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 50, height: 6, bgcolor: 'success.main', borderRadius: 1 }} />
                                <Typography variant="caption">0-2: Easy to rank organically</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 50, height: 6, bgcolor: 'warning.main', borderRadius: 1 }} />
                                <Typography variant="caption">2-3: Moderate competition</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 50, height: 6, bgcolor: 'error.main', borderRadius: 1 }} />
                                <Typography variant="caption">3-5: Heavy SERP feature competition</Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Popover>
                      </Box>
                    )}

                    {/* SERP Results - Dynamic feature order matching Google layout */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {(() => {
                        // Feature styling configuration
                        const featureConfig: Record<string, { icon: React.ReactNode; label: string; description: string; bgcolor: string; borderColor: string; textColor: string }> = {
                          ai_overview: { icon: <AiOverviewIcon sx={{ color: '#4285F4', fontSize: 20 }} />, label: 'AI Overview', description: "Google's AI-generated summary", bgcolor: '#E8F0FE', borderColor: '#4285F4', textColor: '#4285F4' },
                          featured_snippet: { icon: <FeaturedSnippetIcon sx={{ color: '#9C27B0', fontSize: 20 }} />, label: 'Featured Snippet', description: 'Highlighted answer box (Position 0)', bgcolor: '#F3E5F5', borderColor: '#9C27B0', textColor: '#7B1FA2' },
                          knowledge_graph: { icon: <KnowledgeGraphIcon sx={{ color: '#1976D2', fontSize: 20 }} />, label: 'Knowledge Panel', description: 'Info panel (sidebar)', bgcolor: '#E3F2FD', borderColor: '#1976D2', textColor: '#1565C0' },
                          local_pack: { icon: <LocalPackIcon sx={{ color: '#4CAF50', fontSize: 20 }} />, label: 'Local Pack', description: 'Map with local listings', bgcolor: '#E8F5E9', borderColor: '#4CAF50', textColor: '#388E3C' },
                          shopping: { icon: <ShoppingIcon sx={{ color: '#F9A825', fontSize: 20 }} />, label: 'Shopping Results', description: 'Product carousel with prices', bgcolor: '#FFF8E1', borderColor: '#F9A825', textColor: '#F57C00' },
                          ads: { icon: <AdsIcon sx={{ color: '#FF5722', fontSize: 20 }} />, label: 'Sponsored Results', description: 'Paid ads', bgcolor: '#FBE9E7', borderColor: '#FF5722', textColor: '#E64A19' },
                          images: { icon: <ImageIcon sx={{ color: '#E91E63', fontSize: 20 }} />, label: 'Image Results', description: 'Image carousel', bgcolor: '#FCE4EC', borderColor: '#E91E63', textColor: '#C2185B' },
                          videos: { icon: <VideoIcon sx={{ color: '#F44336', fontSize: 20 }} />, label: 'Video Results', description: 'Video carousel', bgcolor: '#FFEBEE', borderColor: '#F44336', textColor: '#D32F2F' },
                          people_also_ask: { icon: <PaaIcon sx={{ color: '#FF9800', fontSize: 20 }} />, label: 'People Also Ask', description: 'Expandable questions', bgcolor: '#FFF3E0', borderColor: '#FF9800', textColor: '#F57C00' },
                        };

                        // Render a SERP feature card with optional content details
                        // featureItem contains: { feature, position, apiKey, ads? }
                        const renderFeatureCard = (featureItem: any, key: string) => {
                          const config = featureConfig[featureItem.feature];
                          if (!config) return null;

                          // Get feature-specific content - ads come from featureItem directly (set by backend)
                          const featureData = serpResults.serp_features?.[featureItem.feature as keyof typeof serpResults.serp_features];
                          const videos = featureItem.feature === 'videos' && Array.isArray(featureData) ? featureData : null;
                          // Ads are embedded in the featureItem by the backend
                          const ads = featureItem.feature === 'ads' && featureItem.ads;

                          return (
                            <Card key={key} variant="outlined" sx={{ p: 1.5, bgcolor: config.bgcolor, borderColor: config.borderColor, borderStyle: 'dashed' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: (videos || ads) ? 1 : 0 }}>
                                {config.icon}
                                <Typography variant="body2" fontWeight={600} sx={{ color: config.textColor }}>
                                  {config.label}
                                </Typography>
                                {featureItem.position === 'top' && <Chip label="TOP" size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem', bgcolor: config.borderColor, color: 'white' }} />}
                                {featureItem.position === 'bottom' && <Chip label="BOTTOM" size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem', bgcolor: 'grey.500', color: 'white' }} />}
                                {featureItem.position === 'sidebar' && <Chip label="SIDEBAR" size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem', bgcolor: 'grey.400', color: 'white' }} />}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                  {config.description}
                                </Typography>
                              </Box>

                              {/* Video content details - show ALL videos */}
                              {videos && videos.length > 0 && (
                                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {videos.map((video: any, i: number) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 16 }}>{i + 1}.</Typography>
                                      <Link href={video.url} target="_blank" variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {video.title}
                                      </Link>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {video.channel}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}

                              {/* Ad content details - show ALL ads */}
                              {ads && Array.isArray(ads) && ads.length > 0 && (
                                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {ads.map((ad: any, i: number) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 16 }}>{i + 1}.</Typography>
                                      <Link href={ad.link} target="_blank" variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {ad.title}
                                      </Link>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {ad.source || (ad.link ? new URL(ad.link).hostname : '')}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Card>
                          );
                        };

                        // Use dynamic feature_order from API - render in EXACT order from serphouse
                        const featureOrder = serpResults.serp_features?.feature_order || [];
                        const foldPosition = device === 'mobile' ? 3 : 5;

                        // Filter to only show features that are actually present
                        const hasFeature = (f: string) => {
                          // For ads, check has_top_ads (backend sets this for both top and bottom ads)
                          if (f === 'ads') return serpResults.serp_features?.has_top_ads === true;
                          const key = `has_${f}` as keyof SerpFeatures;
                          return serpResults.serp_features?.[key] === true;
                        };

                        // Split feature_order by organic position - render in exact API order
                        const organicIndex = featureOrder.findIndex((f: any) => f.feature === 'organic');
                        const beforeOrganic = organicIndex > 0 ? featureOrder.slice(0, organicIndex) : [];
                        const afterOrganic = organicIndex >= 0 ? featureOrder.slice(organicIndex + 1) : [];

                        // Active features before organic (ads with position embedded)
                        // Exclude related_searches and people_also_ask (shown in detailed sections below)
                        const activeBeforeOrganic = beforeOrganic.filter((f: any) =>
                          hasFeature(f.feature) && f.feature !== 'related_searches' && f.feature !== 'people_also_ask'
                        );
                        // Active features after organic
                        const activeAfterOrganic = afterOrganic.filter((f: any) =>
                          hasFeature(f.feature) && f.feature !== 'related_searches' && f.feature !== 'people_also_ask'
                        );

                        return (
                          <>
                            {/* Features BEFORE organic results - exact API order */}
                            {activeBeforeOrganic.map((item: any, idx: number) => renderFeatureCard(item, `before-${item.feature}-${idx}`))}

                            {/* Organic Results */}
                            {serpResults.organic_results?.slice(0, 10).map((result, idx) => {
                              const isOurs = isOurWebsite(result.link);
                              const isOfficial = isOfficialWebsite(result.link);
                              const forum = isForumSite(result.link);
                              const isFold = idx === foldPosition;

                              // Forum platform colors and icons
                              const forumStyles: Record<string, { color: string; bgcolor: string; icon: string }> = {
                                reddit: { color: '#FF4500', bgcolor: '#FFF4F0', icon: '🔴' },
                                facebook: { color: '#1877F2', bgcolor: '#F0F4FF', icon: '🔵' },
                                quora: { color: '#B92B27', bgcolor: '#FFF4F4', icon: '🔴' }
                              };
                              const forumStyle = forum.platform ? forumStyles[forum.platform] : null;

                              return (
                                <React.Fragment key={idx}>
                                  {isFold && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                      <Divider sx={{ flex: 1 }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        THE FOLD
                                      </Typography>
                                      <Divider sx={{ flex: 1 }} />
                                    </Box>
                                  )}
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      border: isOurs ? '2px solid' : '1px solid',
                                      borderColor: isOurs ? 'success.main' : isOfficial ? 'warning.light' : forum.isForum && forumStyle ? forumStyle.color : 'divider',
                                      bgcolor: isOurs ? 'success.50' : forum.isForum && forumStyle ? forumStyle.bgcolor : 'background.paper',
                                      opacity: (isOfficial && !isOurs) || forum.isForum ? 0.7 : 1,
                                      position: 'relative'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                                      <Chip
                                        label={result.position}
                                        size="small"
                                        color={isOurs ? 'success' : 'default'}
                                        sx={{ minWidth: 28, height: 22, fontSize: '0.75rem' }}
                                      />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Link
                                          href={result.link}
                                          target="_blank"
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {result.title}
                                        </Link>
                                        <Typography variant="caption" color="success.main" display="block" noWrap>
                                          {result.displayed_link}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          {result.snippet}
                                        </Typography>
                                      </Box>
                                      {isOurs && (
                                        <Chip label="OURS!" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                                      )}
                                      {isOfficial && !isOurs && (
                                        <Chip label="Official" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'warning.light' }} />
                                      )}
                                      {forum.isForum && forumStyle && (
                                        <Chip
                                          label={`${forumStyle.icon} ${forum.platform?.charAt(0).toUpperCase()}${forum.platform?.slice(1)}`}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            bgcolor: forumStyle.color,
                                            color: 'white',
                                            fontWeight: 600
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Card>
                                </React.Fragment>
                              );
                            })}

                            {/* Features AFTER organic results - exact API order (bottom ads, etc.) */}
                            {activeAfterOrganic.map((item: any, idx: number) => renderFeatureCard(item, `after-${item.feature}-${idx}`))}
                          </>
                        );
                      })()}
                    </Box>

                    {/* People Also Ask Details - moved from inline to detailed section */}
                    {serpResults.people_also_ask && serpResults.people_also_ask.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            <QuestionIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                            People Also Ask
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setTabValue(2)}
                            endIcon={<TreeIcon sx={{ fontSize: 14 }} />}
                          >
                            Deep Dive PAA
                          </Button>
                        </Box>
                        {serpResults.people_also_ask.slice(0, 4).map((item, idx) => (
                          <Typography key={idx} variant="body2" sx={{ mb: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                            {item.question}
                          </Typography>
                        ))}
                      </Paper>
                    )}

                    {/* Related Searches */}
                    {serpResults.related_searches && serpResults.related_searches.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Related Searches
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {serpResults.related_searches.slice(0, 8).map((item, idx) => (
                            <Chip
                              key={idx}
                              label={item.query}
                              onClick={() => handleKeywordClick(item.query)}
                              clickable
                              size="small"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Deep Dive Actions */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PeopleIcon />}
                        onClick={() => setTabValue(1)}
                        fullWidth
                      >
                        Analyze Competitors
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TreeIcon />}
                        onClick={() => setTabValue(2)}
                        fullWidth
                      >
                        Explore PAA Trees
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                    <SpeedIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">Click a keyword to see SERP results</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Live Google SERP Screenshot - Full width below both panels */}
        {serpResults && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 3,
              bgcolor: 'grey.50',
              borderColor: 'primary.light',
              borderStyle: 'dashed'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                Live Google SERP Preview
              </Typography>
              {serpScreenshotLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading SERP snapshot...
                  </Typography>
                </Box>
              )}
              {serpScreenshot && !serpScreenshotLoading && (
                <Chip
                  label="Live from Google"
                  size="small"
                  color="success"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>

            {serpScreenshot ? (
              <Box
                sx={{
                  height: 600,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white',
                  overflow: 'hidden'
                }}
              >
                <iframe
                  srcDoc={serpScreenshot}
                  title="Google SERP Snapshot"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  sandbox="allow-same-origin allow-scripts"
                />
              </Box>
            ) : serpScreenshotLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={32} sx={{ mb: 1.5 }} />
                  <Typography variant="body2" color="text.secondary" display="block">
                    Loading live Google SERP snapshot...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This may take a few seconds
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 150, bgcolor: 'grey.100', borderRadius: 1, gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  SERP snapshot will load automatically
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (Cached results may not have visual preview)
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Compare the live Google results above with our parsed SERP features to verify accuracy
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Tab 1: Competitors */}
      <TabPanel value={tabValue} index={1}>
        {/* View Mode Toggle */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ToggleButtonGroup
                value={competitorsViewMode}
                exclusive
                onChange={(e, v) => v && setCompetitorsViewMode(v)}
                size="small"
              >
                <ToggleButton value="keyword" disabled={!activeKeywordContext}>
                  <SearchIcon sx={{ mr: 1, fontSize: 18 }} />
                  This Keyword
                </ToggleButton>
                <ToggleButton value="historical">
                  <HistoryIcon sx={{ mr: 1, fontSize: 18 }} />
                  All Historical
                </ToggleButton>
              </ToggleButtonGroup>
              {activeKeywordContext && competitorsViewMode === 'keyword' && (
                <Chip
                  label={`"${activeKeywordContext}"`}
                  color="primary"
                  variant="outlined"
                  onDelete={() => setCompetitorsViewMode('historical')}
                />
              )}
            </Box>
            {!activeKeywordContext && competitorsViewMode === 'keyword' && (
              <Alert severity="info" sx={{ py: 0, flexGrow: 1 }}>
                Research a keyword first to see keyword-specific competitors
              </Alert>
            )}
          </Box>
        </Paper>

        {/* Keyword-specific Competitors View */}
        {competitorsViewMode === 'keyword' && activeKeywordContext && cachedKeywordData ? (
          <>
            <Typography variant="h6" gutterBottom>
              Competitors for "{activeKeywordContext}"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Domains ranking in the top 10 for this keyword
            </Typography>

            <Grid container spacing={2}>
              {cachedKeywordData.competitorDomains.slice(0, 10).map((competitor, idx) => {
                const isOurs = isOurDomain(competitor.domain);
                const isOfficial = isOfficialDomainMatch(competitor.domain);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                    <Card sx={{
                      height: '100%',
                      border: isOurs ? '2px solid' : isOfficial ? '1px solid' : 'none',
                      borderColor: isOurs ? 'success.main' : 'grey.300',
                      opacity: isOfficial ? 0.85 : 1,
                      position: 'relative'
                    }}>
                      {isOurs && (
                        <Chip
                          label="Our Site"
                          size="small"
                          color="success"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        />
                      )}
                      {isOfficial && !isOurs && (
                        <Chip
                          label="Official"
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'grey.700', color: 'white' }}
                        />
                      )}
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              color: competitor.position <= 3 ? 'success.main' : competitor.position <= 5 ? 'warning.main' : 'text.secondary',
                              fontWeight: 700,
                              minWidth: 40
                            }}
                          >
                            #{competitor.position}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap fontWeight={600}>
                              {competitor.domain}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {competitor.title}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          href={competitor.url}
                          target="_blank"
                          fullWidth
                          sx={{ mt: 1 }}
                        >
                          View Page
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Quick Stats */}
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Quick Stats</Typography>
                <Tooltip title="Refresh rankings (bypass cache)">
                  <IconButton
                    onClick={handleRefreshRankings}
                    disabled={serpLoading}
                    size="small"
                    sx={{ color: 'primary.main' }}
                  >
                    <RefreshIcon sx={{ animation: serpLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {cachedKeywordData.competitorDomains.filter(c =>
                          isOurDomain(c.domain)
                        ).length > 0 ?
                          cachedKeywordData.competitorDomains.find(c =>
                            isOurDomain(c.domain)
                          )?.position || '-' : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Our Position</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="secondary">
                        {cachedKeywordData.competitorDomains.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Total Results</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {cachedKeywordData.competitorDomains.filter(c =>
                          isOfficialDomainMatch(c.domain)
                        ).length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Official Sites</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {cachedKeywordData.competitorDomains.filter(c =>
                          !isOfficialDomainMatch(c.domain) && !isOurDomain(c.domain)
                        ).length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Beatable Competitors</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* AI Analysis Section */}
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">AI Competitor Analysis</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {competitorAnalysis && (
                    <Tooltip title="Re-run analysis (bypass cache)">
                      <IconButton
                        onClick={() => handleCompetitorAnalysis(true)}
                        disabled={analysisLoading}
                        size="small"
                        sx={{ color: '#cc081c' }}
                      >
                        <RefreshIcon sx={{ animation: analysisLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => handleCompetitorAnalysis(false)}
                    disabled={analysisLoading || cachedKeywordData.competitorDomains.filter(c =>
                      !isOfficialDomainMatch(c.domain) && !isOurDomain(c.domain)
                    ).length === 0}
                    startIcon={analysisLoading ? <CircularProgress size={18} color="inherit" /> : <LightbulbIcon />}
                    sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
                  >
                    {analysisLoading ? 'Analyzing...' : (competitorAnalysis ? 'View Analysis' : 'Analyze Why They Rank Higher')}
                  </Button>
                </Box>
              </Box>

              {analysisError && (
                <Alert severity="error" sx={{ mb: 2 }}>{analysisError}</Alert>
              )}

              {!competitorAnalysis && !analysisLoading && !analysisError && (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(123, 97, 255, 0.05)', border: '1px dashed', borderColor: '#cc081c' }}>
                  <LightbulbIcon sx={{ fontSize: 48, color: '#cc081c', mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Get AI-powered insights on why competitors rank higher
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Claude will analyze the content of your page and competitors to provide specific, actionable recommendations
                  </Typography>
                </Paper>
              )}

              {competitorAnalysis && (
                <Box>
                  {/* Summary */}
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Executive Summary</Typography>
                    <Typography variant="body2">{competitorAnalysis.summary}</Typography>
                  </Alert>

                  {/* Content Gaps */}
                  {competitorAnalysis.contentGaps && competitorAnalysis.contentGaps.length > 0 && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Content Gaps
                      </Typography>
                      <List dense>
                        {competitorAnalysis.contentGaps.map((gap: string, idx: number) => (
                          <ListItem key={idx}>
                            <ListItemText
                              primary={gap}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  {/* Quick Wins */}
                  {competitorAnalysis.quickWins && competitorAnalysis.quickWins.length > 0 && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600} color="success.dark">
                        Quick Wins (Can do today)
                      </Typography>
                      <List dense>
                        {competitorAnalysis.quickWins.map((win: string, idx: number) => (
                          <ListItem key={idx}>
                            <CheckCircleIcon sx={{ mr: 1, color: 'success.main', fontSize: 18 }} />
                            <ListItemText
                              primary={win}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  {/* Recommendations */}
                  {competitorAnalysis.recommendations && competitorAnalysis.recommendations.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Detailed Recommendations
                      </Typography>
                      <Grid container spacing={2}>
                        {competitorAnalysis.recommendations.map((rec: any, idx: number) => (
                          <Grid size={{ xs: 12, md: 6 }} key={idx}>
                            <Card sx={{
                              height: '100%',
                              borderLeft: '4px solid',
                              borderLeftColor: rec.type === 'high_priority' ? 'error.main' :
                                rec.type === 'medium_priority' ? 'warning.main' : 'info.main'
                            }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {rec.title}
                                  </Typography>
                                  <Chip
                                    label={rec.type?.replace('_', ' ') || 'recommendation'}
                                    size="small"
                                    color={rec.type === 'high_priority' ? 'error' :
                                      rec.type === 'medium_priority' ? 'warning' : 'info'}
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {rec.description}
                                </Typography>
                                {(rec.effort || rec.impact) && (
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {rec.effort && (
                                      <Chip
                                        label={`Effort: ${rec.effort}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                    {rec.impact && (
                                      <Chip
                                        label={`Impact: ${rec.impact}`}
                                        size="small"
                                        variant="outlined"
                                        color={rec.impact === 'high' ? 'success' : 'default'}
                                      />
                                    )}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Long Term Strategies */}
                  {competitorAnalysis.longTermStrategies && competitorAnalysis.longTermStrategies.length > 0 && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Long-Term Strategies
                      </Typography>
                      <List dense>
                        {competitorAnalysis.longTermStrategies.map((strategy: string, idx: number) => (
                          <ListItem key={idx}>
                            <TrendingUpIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                            <ListItemText
                              primary={strategy}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </>
        ) : competitorsViewMode === 'keyword' && !activeKeywordContext ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Keyword Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Research a keyword in the Research & SERP tab to see competitors for that specific keyword
            </Typography>
            <Button variant="outlined" onClick={() => setTabValue(0)}>
              Go to Research Tab
            </Button>
          </Paper>
        ) : !selectedWebsite ? (
          <Alert severity="info">Please select a website to view competitor analysis</Alert>
        ) : competitorsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : competitorsError ? (
          <Alert severity="error">{competitorsError}</Alert>
        ) : (
          <>
            {/* Insights */}
            {insights.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Key Insights</Typography>
                <Grid container spacing={2}>
                  {insights.slice(0, 4).map((insight, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            {getInsightIcon(insight.type)}
                            <Chip
                              label={insight.priority}
                              color={getPriorityColor(insight.priority) as any}
                              size="small"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                          <Typography variant="subtitle2" gutterBottom>{insight.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {insight.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Competitor Overview Cards */}
            <Typography variant="h6" gutterBottom>Competitor Overview</Typography>
            <Grid container spacing={3}>
              {competitorOverview.map((competitor) => {
                const isOfficial = isOfficialDomain(competitor.domain);
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={competitor.domain}>
                    <Card sx={{
                      opacity: isOfficial ? 0.85 : 1,
                      border: isOfficial ? '1px solid' : 'none',
                      borderColor: 'grey.300'
                    }}>
                      {isOfficial && (
                        <Chip
                          label="Official"
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'grey.700', color: 'white' }}
                        />
                      )}
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ flex: 1 }}>{competitor.name}</Typography>
                          <IconButton size="small" onClick={() => window.open(`https://${competitor.domain}`, '_blank')}>
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{competitor.domain}</Typography>
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="primary">{competitor.totalKeywords}</Typography>
                              <Typography variant="caption" color="text.secondary">Keywords</Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="secondary">{competitor.avgPosition.toFixed(1)}</Typography>
                              <Typography variant="caption" color="text.secondary">Avg Position</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TrophyIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                            <Typography variant="body2">{competitor.topPositions} top 3 positions</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Visibility</Typography>
                            <Typography variant="caption">{competitor.visibility.toFixed(1)}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={competitor.visibility} sx={{ height: 6, borderRadius: 3 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Keyword Competition Table */}
            {keywordCompetition.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>Keyword Competition</Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Keyword</TableCell>
                        <TableCell align="center">Our Position</TableCell>
                        <TableCell align="center">Search Volume</TableCell>
                        <TableCell>Top Competitors</TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {keywordCompetition.slice(0, 20).map((kc) => (
                        <React.Fragment key={kc.keyword}>
                          <TableRow hover>
                            <TableCell>
                              <Link
                                component="button"
                                onClick={() => handleKeywordClick(kc.keyword)}
                                sx={{ textAlign: 'left' }}
                              >
                                {kc.keyword}
                              </Link>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={kc.ourPosition || 'Not ranking'}
                                color={kc.ourPosition && kc.ourPosition <= 10 ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {kc.searchVolume?.toLocaleString() || '-'}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {kc.competitors.slice(0, 3).map((comp, idx) => (
                                  <Chip
                                    key={idx}
                                    label={`#${comp.position} ${comp.domain}`}
                                    size="small"
                                    variant="outlined"
                                    color={comp.position < (kc.ourPosition || 999) ? 'error' : 'default'}
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => toggleKeywordExpansion(kc.keyword)}>
                                {expandedKeywords.has(kc.keyword) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0 }}>
                              <Collapse in={expandedKeywords.has(kc.keyword)}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle2" gutterBottom>All Competitors</Typography>
                                  {kc.competitors.map((comp, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Chip label={`#${comp.position}`} size="small" />
                                      <Typography variant="body2">{comp.domain}</Typography>
                                      <Link href={comp.url} target="_blank" sx={{ ml: 'auto' }}>
                                        <LaunchIcon fontSize="small" />
                                      </Link>
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab 2: PAA Trees */}
      <TabPanel value={tabValue} index={2}>
        {/* View Mode Toggle */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ToggleButtonGroup
                value={paaViewMode}
                exclusive
                onChange={(e, v) => v && setPaaViewMode(v)}
                size="small"
              >
                <ToggleButton value="keyword" disabled={!activeKeywordContext}>
                  <SearchIcon sx={{ mr: 1, fontSize: 18 }} />
                  This Keyword
                </ToggleButton>
                <ToggleButton value="historical">
                  <HistoryIcon sx={{ mr: 1, fontSize: 18 }} />
                  All Trees
                </ToggleButton>
              </ToggleButtonGroup>
              {activeKeywordContext && paaViewMode === 'keyword' && (
                <Chip
                  label={`"${activeKeywordContext}"`}
                  color="primary"
                  variant="outlined"
                  onDelete={() => setPaaViewMode('historical')}
                />
              )}
            </Box>
            {paaViewMode === 'historical' && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Search PAA trees..."
                  value={paaSearchQuery}
                  onChange={(e) => setPaaSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  }}
                />
                <IconButton onClick={loadPaaTrees}><RefreshIcon /></IconButton>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Keyword-specific PAA View */}
        {paaViewMode === 'keyword' && activeKeywordContext && cachedKeywordData ? (
          <>
            <Typography variant="h6" gutterBottom>
              People Also Ask for "{activeKeywordContext}"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Questions from Google's SERP for this keyword
            </Typography>

            {cachedKeywordData.paaQuestions.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {cachedKeywordData.paaQuestions.map((question, idx) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <QuestionIcon sx={{ color: '#cc081c', mt: 0.5 }} />
                            <Typography variant="body1">
                              {question}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Generate Full Tree CTA */}
                <Paper sx={{ p: 3, mt: 4, textAlign: 'center', bgcolor: 'rgba(123, 97, 255, 0.05)', border: '1px dashed', borderColor: '#cc081c' }}>
                  <TreeIcon sx={{ fontSize: 48, color: '#cc081c', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Want deeper insights?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Generate a full PAA tree to explore all related questions and build a comprehensive content strategy
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/paa-trees?generate=${encodeURIComponent(activeKeywordContext)}&country=${country}`)}
                    sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
                  >
                    Generate Full PAA Tree
                  </Button>
                </Paper>
              </>
            ) : (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <QuestionIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No PAA Questions Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Google didn't return any "People Also Ask" questions for this keyword in the SERP
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/paa-trees?generate=${encodeURIComponent(activeKeywordContext)}&country=${country}`)}
                >
                  Generate PAA Tree Anyway
                </Button>
              </Paper>
            )}
          </>
        ) : paaViewMode === 'keyword' && !activeKeywordContext ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Keyword Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Research a keyword in the Research & SERP tab to see PAA questions for that specific keyword
            </Typography>
            <Button variant="outlined" onClick={() => setTabValue(0)}>
              Go to Research Tab
            </Button>
          </Paper>
        ) : !selectedWebsite ? (
          <Alert severity="info">Please select a website to view PAA trees</Alert>
        ) : (
          <>
            {paaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : paaError ? (
              <Alert severity="error">{paaError}</Alert>
            ) : filteredPaaTrees.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <TreeIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No PAA Trees Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PAA trees will appear here once they are generated
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredPaaTrees.map((tree) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={tree.tree_id}>
                    <Card sx={{ height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                          {tree.root_keyword}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip label={`${getCountryFlag(tree.country)} ${tree.country}`} size="small" variant="outlined" />
                          <Chip
                            label={tree.status || tree.processing_status}
                            size="small"
                            color={
                              (tree.status || tree.processing_status) === 'completed' ? 'success' :
                              (tree.status || tree.processing_status) === 'processing' ? 'warning' : 'error'
                            }
                          />
                          {tree.total_questions && tree.total_questions > 0 && (
                            <Chip
                              icon={<QuestionIcon sx={{ fontSize: 16 }} />}
                              label={`${tree.total_questions} questions`}
                              size="small"
                              sx={{ bgcolor: 'rgba(123, 97, 255, 0.1)' }}
                            />
                          )}
                        </Box>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => navigate(`/paa-trees/${tree.tree_id}`)}
                          disabled={tree.processing_status === 'failed' || tree.processing_status === 'processing'}
                          sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
                        >
                          {tree.processing_status === 'failed' ? 'No Data' :
                           tree.processing_status === 'processing' ? 'Processing...' : 'View Tree'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </TabPanel>

      {/* Drill-down Drawer for SERP view */}
      <Drawer
        anchor="right"
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', md: '50%' }, maxWidth: 700 } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => setDrillDownOpen(false)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">SERP for "{drillDownKeyword}"</Typography>
          </Box>

          {serpLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
          ) : serpError ? (
            <Alert severity="error">{serpError}</Alert>
          ) : serpResults ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {serpResults.organic_results?.slice(0, 10).map((result, idx) => {
                const isOurs = isOurWebsite(result.link);
                const forum = isForumSite(result.link);
                const forumStyles: Record<string, { color: string; bgcolor: string; icon: string }> = {
                  reddit: { color: '#FF4500', bgcolor: '#FFF4F0', icon: '🔴' },
                  facebook: { color: '#1877F2', bgcolor: '#F0F4FF', icon: '🔵' },
                  quora: { color: '#B92B27', bgcolor: '#FFF4F4', icon: '🔴' }
                };
                const forumStyle = forum.platform ? forumStyles[forum.platform] : null;
                return (
                  <Card
                    key={idx}
                    variant="outlined"
                    sx={{
                      border: isOurs ? '2px solid' : '1px solid',
                      borderColor: isOurs ? 'success.main' : forum.isForum && forumStyle ? forumStyle.color : 'divider',
                      bgcolor: isOurs ? 'success.50' : forum.isForum && forumStyle ? forumStyle.bgcolor : 'background.paper',
                      opacity: forum.isForum ? 0.7 : 1
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                        <Chip label={result.position} size="small" color={isOurs ? 'success' : 'default'} />
                        <Box sx={{ flex: 1 }}>
                          <Link href={result.link} target="_blank" variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {result.title}
                          </Link>
                          <Typography variant="caption" color="success.main" display="block">
                            {result.displayed_link}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {result.snippet}
                          </Typography>
                        </Box>
                        {forum.isForum && forumStyle && (
                          <Chip
                            label={`${forumStyle.icon} ${forum.platform?.charAt(0).toUpperCase()}${forum.platform?.slice(1)}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: forumStyle.color,
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </Drawer>

      {/* Save Keywords to URL Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon sx={{ color: '#cc081c' }} />
            Save Keywords to URL
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Save {selectedKeywords.size} selected keyword{selectedKeywords.size !== 1 ? 's' : ''} to a specific URL for tracking.
          </Typography>

          {/* Website Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              options={availableWebsites}
              getOptionLabel={(option) => option.base_url?.replace(/^https?:\/\/(www\.)?/, '') || ''}
              value={selectedTargetWebsite}
              onChange={(_, newValue) => setSelectedTargetWebsite(newValue)}
              loading={loadingWebsites}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Website"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingWebsites ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    {option.base_url?.replace(/^https?:\/\/(www\.)?/, '')}
                  </Box>
                </li>
              )}
            />
          </FormControl>

          {/* URL Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              options={availableUrls}
              getOptionLabel={(option) => option.path || option.full_url || ''}
              value={selectedTargetUrl}
              onChange={(_, newValue) => setSelectedTargetUrl(newValue)}
              loading={loadingUrls}
              disabled={!selectedTargetWebsite}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select URL"
                  placeholder={selectedTargetWebsite ? 'Search for a URL...' : 'Select a website first'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUrls ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {option.path || option.full_url}
                  </Typography>
                </li>
              )}
            />
          </FormControl>

          {/* Selected Keywords Preview */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Keywords to save:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from(selectedKeywords).slice(0, 10).map((kw, idx) => (
                <Chip key={idx} label={kw} size="small" variant="outlined" />
              ))}
              {selectedKeywords.size > 10 && (
                <Chip label={`+${selectedKeywords.size - 10} more`} size="small" />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveKeywordsToUrl}
            disabled={!selectedTargetWebsite || !selectedTargetUrl || savingKeywords}
            startIcon={savingKeywords ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
          >
            {savingKeywords ? 'Saving...' : 'Save Keywords'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KeywordIntelligencePage;
